import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { Link } from '@/components/shared/links/Link'
import { SmallSpinner } from '@/components/shared/Spinner/SmallSpinner'
import { Markdown } from '@/components/shared/text/Markdown'
import type { TodoId } from '@/data/processingTypes/todoId.generated.const'
import { campaigns } from '@/data/radinfra-de/campaigns'
import { buildHashtags } from '@/data/radinfra-de/utils/buildHashtags'
import { buildTaskInstructions } from '@/data/radinfra-de/utils/buildTaskInstructions'
import { osmEditIdUrl, osmEditJosmUrl } from '../Tools/osmUrls/osmUrls'
// import { pointFromGeometry } from '../Tools/osmUrls/pointFromGeometry'
import type { NoticeMaproulette } from './NoticeMaproulette'

// const maprouletteStatus = new Map([
//   [0, 'Offen'],
//   [1, 'Erledigt'],
//   [2, 'Erledigt (war kein Problem)'],
//   [3, 'Offen (übersprungen)'],
//   [4, 'Gelöscht'],
//   [5, 'Erledigt (war bereits erledigt)'],
//   [6, 'Offen (zu schwer?)'],
// ])
const maprouletteStatusCompleted = [1, 2, 4, 5]

const maprouletteTaskSchema = z.object({
  id: z.number(),
  // https://maproulette-python-client.readthedocs.io/en/latest/usage/functionality.html
  //  0 = Created, 1 = Fixed, 2 = False Positive, 3 = Skipped, 4 = Deleted, 5 = Already Fixed, 6 = Too Hard
  status: z.number(),
  location: z.object({
    type: z.literal('Point'),
    coordinates: z.tuple([z.number(), z.number()]),
  }),
})

const fetchMapRouletteTask = async (
  mapRouletteId: number | null | undefined,
  osmTypeIdString: string | undefined,
) => {
  const url = `https://maproulette.org/api/v2/challenge/${mapRouletteId || 'MISSING'}/task/${encodeURIComponent(osmTypeIdString || 'MISSING')}`
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  const json = await response.json()
  return maprouletteTaskSchema.parse(json)
}

type Props = { projectKey: TodoId } & Omit<NoticeMaproulette, 'sourceId'> & {
    osmTypeIdString: string
  }

export const NoticeMaprouletteTask = ({
  projectKey,
  osmTypeIdString,
  kind,
  properties,
  geometry,
}: Props) => {
  const radinfraCampaign = campaigns?.find((c) => c.id === projectKey)
  const maprouletteCampaign =
    radinfraCampaign?.maprouletteChallenge.enabled === true ? radinfraCampaign : undefined
  const mapRouletteId =
    maprouletteCampaign?.maprouletteChallenge?.enabled === true
      ? maprouletteCampaign?.maprouletteChallenge?.id
      : undefined

  const showMaproulette =
    radinfraCampaign?.recommendedAction === 'maproulette' &&
    radinfraCampaign?.maprouletteChallenge.enabled === true
  const showStreetcomplete = radinfraCampaign?.recommendedAction === 'streetcomplete'
  // const showEditor = radinfraCampaign?.recommendedAction === 'map'

  const { data, isLoading } = useQuery({
    queryKey: ['mapRouletteTask', mapRouletteId, properties.id],
    queryFn: () => fetchMapRouletteTask(mapRouletteId, properties.id),
    enabled: !!mapRouletteId,
  })

  if (geometry?.type !== 'LineString') return null

  const text = buildTaskInstructions({
    projectKey,
    osmTypeIdString,
    kind: kind || 'UNKOWN', // Fallback is needed because TS cannot know that we only use this when the `kind` is known
    geometry: geometry, // Guarded above
  })

  if (!text) {
    return <p>(Die Aufgabenbeschreibung ist noch in Arbeit)</p>
  }

  // Hidden while the MapRoulette integration in iD and Rapid is broken. Users start on maproulette.org instead.
  // The location of the MR pin is the best we can use, but we can always fall back to the one we use internally elsewhere
  // const [centerLng, centerLat] = data?.location?.coordinates || pointFromGeometry(geometry)
  // const paramsMap = `map=19.5/${centerLat}/${centerLng}`
  // const paramsDisabled =
  //   'disable_features=points,building_parts,indoor,boundaries,pistes,aerialways,power'
  // const paramsMaproulette = `maproulette=${mapRouletteId}`
  // const paramsMapillary = projectKey.endsWith('__mapillary') ? 'photo_dates=2024-02-23_' : undefined
  // const campaignParams = [paramsMap, paramsMaproulette, paramsDisabled, paramsMapillary].filter(
  //   Boolean,
  // )
  // const rapidCampaignLink = `https://rapideditor.org/edit#${campaignParams.join('&')}`
  // Experimental MapRoulette-in-iD (https://github.com/tordans/iD/pull/4)
  // const idMaprouletteCampaignLink = `https://deploy-preview-4--tordans-id-experiments.netlify.app/#${campaignParams.join('&')}`

  const maprouletteTaskLink = isLoading
    ? undefined
    : data?.id
      ? `https://maproulette.org/challenge/${mapRouletteId}/task/${data.id}`
      : undefined

  const maprouletteCampaignLink = `https://maproulette.org/browse/challenges/${mapRouletteId}`

  const [osmType, osmId] = osmTypeIdString.split('/') as ['way' | 'node' | 'relation', string] // we know this is true
  const osmEditIdUrlHref = osmEditIdUrl({
    osmType,
    osmId,
    comment:
      radinfraCampaign?.maprouletteChallenge.enabled === true
        ? radinfraCampaign.maprouletteChallenge.checkinComment
        : undefined,
    hashtags: buildHashtags(
      radinfraCampaign?.id,
      radinfraCampaign?.category,
      radinfraCampaign?.maprouletteChallenge.enabled === true,
    )?.join(','),
    source: 'radinfra_de',
  })
  const osmEditJosmUrlHref = osmEditJosmUrl({ osmType, osmId })
  // const osmEditKyleKiwiIdUrlHref = osmEditKyleKiwiIdUrl({ osmType, osmId })
  const completed = data?.status && maprouletteStatusCompleted.includes(data.status)

  return (
    <>
      {!!mapRouletteId && (
        <p className="mb-2 text-right text-xs">
          <Link href={maprouletteCampaignLink} title="MapRoulette" className="text-xs" blank>
            MR #{mapRouletteId}
          </Link>
        </p>
      )}
      <div className="mb-4 flex flex-col items-center gap-1.5 rounded-sm bg-white/80 p-3">
        {showMaproulette && (
          <>
            {/* <Link href={idMaprouletteCampaignLink} blank button>
              Kampagne im iD Editor bearbeiten
            </Link> */}
            {/* {osmEditIdUrlHref && (
              <Link href={osmEditIdUrlHref} blank button>
                Kampagne bearbeiten
              </Link>
            )} */}
            {isLoading ? (
              <span className="flex items-center gap-2 text-gray-400">
                <SmallSpinner /> Lade MapRoulette-Link…
              </span>
            ) : maprouletteTaskLink ? (
              <>
                {completed ? <strong>🎉 Die Aufgabe wurde bereits erledigt.</strong> : null}
                <Link href={maprouletteTaskLink} blank button={!completed}>
                  {completed ? 'MapRoulette öffnen' : 'Als MapRoulette Aufgabe bearbeiten'}
                </Link>
              </>
            ) : (
              <Link href={maprouletteCampaignLink} blank button>
                Kampagne in MapRoulette öffnen
              </Link>
            )}
          </>
        )}
        {showStreetcomplete && (
          <Link href="https://radinfra.de/mitmachen/streetcomplete/" blank button>
            Tipp: Nutze StreetComplete für diese Daten
          </Link>
        )}
        <div className="space-x-2">
          {osmEditIdUrlHref && (
            <Link
              href={osmEditIdUrlHref}
              blank
              button={
                showMaproulette
                  ? false
                  : showStreetcomplete
                    ? false
                    : isLoading === false && !maprouletteTaskLink
              }
            >
              Bearbeiten im iD Editor
            </Link>
          )}
          {/* <Link href={rapidCampaignLink} blank>
            Rapid
          </Link> */}
          {osmEditJosmUrlHref && (
            <Link href={osmEditJosmUrlHref} blank>
              JOSM
            </Link>
          )}
          {/* {osmEditKyleKiwiIdUrlHref && (
            <Link href={osmEditKyleKiwiIdUrlHref} blank>
              kiwiD
            </Link>
          )} */}
        </div>
      </div>
      <Markdown
        markdown={text}
        className="prose-sm marker:text-purple-700 [&>p:first-child]:mt-0"
      />
    </>
  )
}
